import { useMemo } from 'react';
import BreakdownTable from '@/components/ui/breakdown-tables/BreakdownTable';
import type {
  BreakdownCell,
  BreakdownCellContext,
} from '@/components/ui/breakdown-tables/BreakdownTable';
import type {
  CalculatedBreakdown,
  PaymentAllocation,
} from '@/api/rebates/types';
import {
  aggregateCellValues,
  filterCellsForAggregation,
  getAllocationStatus,
  renderAllocationCell,
  type AllocationCellData,
} from '@/utils/rebates/allocationAggregation';
import {
  formatDatesForDisplay,
  extractColumnOrderFromDateLabels,
  buildAllocationKey,
} from '@/utils/rebates/allocationTableHelpers';

interface EditableAllocationBreakdownTableProps {
  rules: Array<{ id: string; name: string }>;
  divisions: Array<{ id: string; name: string }>;
  dates: string[]; // YYYY-MM-DD format dates (end-of-month dates)
  calculatedBreakdowns: CalculatedBreakdown[]; // Expected rebate amounts
  globalAllocations: PaymentAllocation[]; // All allocations for agreement (for global context)
  currentAllocations: Record<string, number>; // Key: `${ruleId}|${divisionId}|${date}`, Value: allocated amount
  onAllocationChange: (
    ruleId: string,
    divisionId: string,
    date: string,
    amount: number,
  ) => void;
  currentPaymentId?: string; // Exclude current payment from global allocations display
}

/**
 * Editable breakdown table for payment allocation
 * Shows rule × division × time breakdown with editable cells
 * Each cell displays: input value (top), expected rebate (middle), delta (bottom)
 */
export function EditableAllocationBreakdownTable({
  rules,
  divisions,
  dates,
  calculatedBreakdowns,
  globalAllocations,
  currentAllocations,
  onAllocationChange,
  currentPaymentId,
}: EditableAllocationBreakdownTableProps) {
  // Build a map of expected rebate amounts: ruleId|divisionId|date -> amount
  const expectedRebatesMap = useMemo(() => {
    const map = new Map<string, number>();
    calculatedBreakdowns.forEach((breakdown) => {
      const key = buildAllocationKey(
        breakdown.ruleId,
        breakdown.divisionId,
        breakdown.date,
      );
      map.set(key, breakdown.rebateAmount);
    });
    return map;
  }, [calculatedBreakdowns]);

  // Build a map of global allocations (excluding current payment): ruleId|divisionId|date -> amount
  const globalAllocationsMap = useMemo(() => {
    const map = new Map<string, number>();
    globalAllocations
      .filter((alloc) => alloc.rebateSupplierPaymentId !== currentPaymentId)
      .forEach((alloc) => {
        const key = buildAllocationKey(
          alloc.rebateRuleId,
          alloc.divisionId,
          alloc.date,
        );
        const existing = map.get(key) || 0;
        map.set(key, existing + alloc.allocatedAmount);
      });
    return map;
  }, [globalAllocations, currentPaymentId]);

  // Format dates for display (preserve chronological order)
  const dateLabels = useMemo(() => {
    return formatDatesForDisplay(dates);
  }, [dates]);

  // Extract column order for BreakdownTable (chronological order)
  const columnOrder = useMemo(() => {
    return extractColumnOrderFromDateLabels(dateLabels);
  }, [dateLabels]);

  // Transform data into flat BreakdownCell[] format
  const data = useMemo(() => {
    const cells: BreakdownCell[] = [];

    // Create cells for each rule × division × date combination
    rules.forEach((rule) => {
      const ruleDivisions = divisions.filter((div) => {
        // Only show divisions that have calculated breakdowns for this rule
        return calculatedBreakdowns.some(
          (bd) => bd.ruleId === rule.id && bd.divisionId === div.id,
        );
      });

      ruleDivisions.forEach((division) => {
        dateLabels.forEach(({ date, label }) => {
          const key = buildAllocationKey(rule.id, division.id, date);
          const expectedRebate = expectedRebatesMap.get(key) || 0;

          cells.push({
            column: label, // Display label (e.g., "Jan 2024")
            subColumn: null, // No column grouping
            row: rule.name, // Rule name
            subRow: division.name, // Division name
            value: expectedRebate, // Expected rebate amount
            metadata: {
              ruleId: rule.id,
              divisionId: division.id,
              date, // Store original date for lookups
              expectedRebate,
            },
          });
        });
      });
    });

    return cells;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rules,
    divisions,
    dateLabels,
    calculatedBreakdowns,
    expectedRebatesMap,
    currentAllocations,
  ]);

  // Aggregate allocation data for a given scope (for total rows and rule-level aggregations)
  const aggregateAllocationData = useMemo(() => {
    return (
      row: string | undefined,
      column: string | undefined,
      isGroupLevel: boolean,
    ): {
      expectedRebate: number;
      currentAllocation: number;
      globalAllocation: number;
      totalAllocation: number;
      delta: number;
    } => {
      let expectedRebate = 0;
      let currentAllocation = 0;
      let globalAllocation = 0;

      // Filter cells that match the aggregation scope
      const matchingCells = filterCellsForAggregation(
        data,
        row,
        column,
        isGroupLevel,
      );

      // Aggregate data from matching cells
      matchingCells.forEach((cell) => {
        const metadata = cell.metadata as
          | {
              ruleId?: string;
              divisionId?: string;
              date?: string;
              expectedRebate?: number;
            }
          | undefined;

        const ruleId = metadata?.ruleId;
        const divisionId = metadata?.divisionId;
        const date = metadata?.date;

        if (ruleId && divisionId && date) {
          const key = buildAllocationKey(ruleId, divisionId, date);
          const cellExpectedRebate = metadata?.expectedRebate || 0;
          const cellCurrentAllocation = currentAllocations[key] || 0;
          const cellGlobalAllocation = globalAllocationsMap.get(key) || 0;

          expectedRebate += cellExpectedRebate;
          currentAllocation += cellCurrentAllocation;
          globalAllocation += cellGlobalAllocation;
        }
      });

      const totalAllocation = currentAllocation + globalAllocation;
      const delta = totalAllocation - expectedRebate;

      return {
        expectedRebate,
        currentAllocation,
        globalAllocation,
        totalAllocation,
        delta,
      };
    };
  }, [data, currentAllocations, globalAllocationsMap]);

  // Aggregation function for cell values (aggregates expected rebates)
  // Uses shared utility function

  // Custom cell renderer for editable allocation cells
  const renderAllocationCellLocal = (context: BreakdownCellContext) => {
    const { cell, row, column, isGroupLevel } = context;
    const metadata = cell.metadata as
      | {
          ruleId?: string;
          divisionId?: string;
          date?: string;
          expectedRebate?: number;
        }
      | undefined;

    const ruleId = metadata?.ruleId;
    const divisionId = metadata?.divisionId;
    const date = metadata?.date;

    // Check if this is an aggregated cell (total row or group level)
    const isAggregated = isGroupLevel || row === '__TOTAL__';

    let expectedRebate: number;
    let currentAllocation: number;
    let globalAllocation: number;
    let totalAllocation: number;
    let delta: number;

    if (isAggregated) {
      // For aggregated cells, calculate totals from all matching cells
      const aggregated = aggregateAllocationData(row, column, isGroupLevel);
      expectedRebate = aggregated.expectedRebate;
      currentAllocation = aggregated.currentAllocation;
      globalAllocation = aggregated.globalAllocation;
      totalAllocation = aggregated.totalAllocation;
      delta = aggregated.delta;
    } else {
      // For individual cells, use the cell's metadata
      if (!ruleId || !divisionId || !date) {
        return null;
      }

      const key = buildAllocationKey(ruleId, divisionId, date);
      expectedRebate = metadata?.expectedRebate || 0;
      currentAllocation = currentAllocations[key] || 0;
      globalAllocation = globalAllocationsMap.get(key) || 0;
      totalAllocation = currentAllocation + globalAllocation;
      delta = totalAllocation - expectedRebate;
    }

    const cellData: AllocationCellData = {
      expectedRebate,
      allocated: currentAllocation,
      globalAllocation,
      delta,
    };

    return renderAllocationCell(context, cellData, {
      editable: true,
      onAllocationChange,
      metadata: {
        ruleId,
        divisionId,
        date,
      },
    });
  };

  return (
    <BreakdownTable
      data={data}
      rowLabel="Rule"
      subRowLabel="Division"
      columnLabel="Month"
      title=""
      subtitle=""
      showCard={false}
      renderCell={renderAllocationCellLocal}
      aggregateCellValues={aggregateCellValues}
      showXDim1Total={false}
      showYDim1Total={true}
      expandAllRows={true}
      fillMissingSubColumns={true}
      fillMissingSubRows={true}
      xDimOrder={columnOrder}
    />
  );
}
